/**
 * Widget público Hub → productos trending de Go Motors.
 * Autocontenido en el theme (sin dependencias del repo inventario-worker).
 */
(function () {
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const cache = new Map();

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatPrice(amount, currency) {
    const value = Number(amount || 0);
    try {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: currency || 'CLP',
        maximumFractionDigits: 0,
      }).format(value);
    } catch (error) {
      return `$${Math.round(value).toLocaleString('es-CL')}`;
    }
  }

  function withUtm(url, params) {
    try {
      const target = new URL(url);
      Object.entries(params).forEach(([key, val]) => {
        if (val) target.searchParams.set(key, val);
      });
      return target.toString();
    } catch (error) {
      return url;
    }
  }

  function buildEndpoint(apiUrl, client, limit) {
    const raw = String(apiUrl || '').trim().replace(/\/$/, '');
    const endpointPath = '/api/public/hub/trending-products';
    const url = raw.includes(endpointPath) ? new URL(raw) : new URL(`${raw}${endpointPath}`);
    url.searchParams.set('client', client || 'gomotors');
    url.searchParams.set('limit', String(limit || 5));
    return url.toString();
  }

  async function fetchTrending(endpoint) {
    const cached = cache.get(endpoint);
    const now = Date.now();
    if (cached && now - cached.at < CACHE_TTL_MS) return cached.data;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Hub ${response.status}: ${text.slice(0, 180)}`);
    }

    const data = await response.json();
    cache.set(endpoint, { at: now, data });
    return data;
  }

  function renderSkeleton(track, count) {
    track.innerHTML = '';
    for (let i = 0; i < count; i += 1) {
      const li = document.createElement('li');
      li.className = 'or-hub-ad__card';
      li.innerHTML = '<div class="or-hub-ad__card-link or-hub-ad__skeleton" aria-hidden="true"></div>';
      track.appendChild(li);
    }
  }

  function renderBrand(root, payload, settings) {
    const nameEl = root.querySelector('[data-or-hub-brand-name]');
    const taglineEl = root.querySelector('[data-or-hub-brand-tagline]');
    const linkEl = root.querySelector('[data-or-hub-brand-link]');
    const logoEl = root.querySelector('[data-or-hub-brand-logo]');

    const brand = payload.brand || {};
    const brandName = settings.brandName || brand.label || brand.name || 'Go Motors';
    const brandUrl = settings.brandUrl || brand.url || 'https://gomotors.cl';
    const tagline = settings.brandTagline || 'Repuestos para tu 4x4';

    if (nameEl) nameEl.textContent = brandName;
    if (taglineEl) taglineEl.textContent = tagline;
    if (linkEl) {
      linkEl.href = withUtm(brandUrl, settings.utm);
      linkEl.textContent = settings.brandCta || 'Ir a Go Motors';
    }

    if (logoEl) {
      if (settings.brandLogo) {
        logoEl.src = settings.brandLogo;
        logoEl.alt = brandName;
        logoEl.hidden = false;
        if (nameEl) nameEl.hidden = true;
      } else {
        logoEl.hidden = true;
        if (nameEl) nameEl.hidden = false;
      }
    }
  }

  function renderProducts(track, products, settings) {
    track.innerHTML = '';
    const list = Array.isArray(products) ? products : [];

    list.forEach((product, index) => {
      const li = document.createElement('li');
      li.className = 'or-hub-ad__card';

      const href = withUtm(product.url, settings.utm);
      const image = product.image
        ? `<img class="or-hub-ad__image" src="${escapeHtml(product.image)}" alt="${escapeHtml(
            product.image_alt || product.title
          )}" loading="lazy" width="160" height="160">`
        : '<div class="or-hub-ad__image-wrap"><span class="or-hub-ad__image-placeholder" aria-hidden="true">GM</span></div>';

      const badge = product.badge
        ? `<span class="or-hub-ad__badge">${escapeHtml(product.badge)}</span>`
        : '';

      li.innerHTML = `
        <a
          class="or-hub-ad__card-link"
          href="${escapeHtml(href)}"
          target="_blank"
          rel="noopener noreferrer sponsored"
          data-or-hub-product
          data-product-id="${escapeHtml(product.id)}"
          data-product-title="${escapeHtml(product.title)}"
          data-product-price="${escapeHtml(product.price)}"
          data-product-position="${index + 1}"
        >
          ${badge}
          <div class="or-hub-ad__image-wrap">${image}</div>
          <span class="or-hub-ad__title">${escapeHtml(product.title)}</span>
          <p class="or-hub-ad__price">${escapeHtml(formatPrice(product.price, product.currency))}</p>
        </a>
      `;

      track.appendChild(li);
    });
  }

  function trackPromotion(eventName, root, product) {
    if (!product) return;
    window.dataLayer = window.dataLayer || [];
    const payload = {
      event: eventName,
      promotion_id: root.dataset.promotionId || 'hub_gomotors_trending',
      promotion_name: root.dataset.promotionName || 'Hub Go Motors Trending',
      creative_name: 'or-hub-ad',
      creative_slot: product.position,
      items: [
        {
          item_id: product.id,
          item_name: product.title,
          price: product.price,
          index: product.position,
          item_list_name: 'Hub Go Motors Trending',
        },
      ],
    };
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  }

  function bindAnalytics(root) {
    if (root.dataset.analyticsBound === 'true') return;
    root.dataset.analyticsBound = 'true';

    root.addEventListener('click', (event) => {
      const link = event.target.closest('[data-or-hub-product]');
      if (!link) return;
      trackPromotion('or_select_promotion', root, {
        id: link.dataset.productId,
        title: link.dataset.productTitle,
        price: Number(link.dataset.productPrice || 0),
        position: Number(link.dataset.productPosition || 0),
      });
    });
  }

  function showState(state, message, visible) {
    if (!state) return;
    state.textContent = message;
    state.hidden = !visible;
  }

  async function loadWidget(root) {
    const apiBase = root.dataset.apiBase;
    if (!apiBase) {
      root.classList.add('or-hub-ad--hidden');
      return;
    }

    const track = root.querySelector('[data-or-hub-track]');
    const state = root.querySelector('[data-or-hub-state]');
    if (!track) return;

    const limit = Number(root.dataset.limit || 5);
    const client = root.dataset.client || 'gomotors';
    const endpoint = buildEndpoint(apiBase, client, limit);
    const settings = {
      brandName: root.dataset.brandName,
      brandTagline: root.dataset.brandTagline,
      brandUrl: root.dataset.brandUrl,
      brandCta: root.dataset.brandCta,
      brandLogo: root.dataset.brandLogo,
      utm: {
        utm_source: root.dataset.utmSource || 'overlandriders',
        utm_medium: root.dataset.utmMedium || 'referral',
        utm_campaign: root.dataset.utmCampaign || 'gomotors_trending',
      },
    };

    root.classList.remove('or-hub-ad--hidden');
    renderSkeleton(track, Math.min(limit, 4));
    showState(state, 'Cargando productos de Go Motors…', true);

    try {
      const payload = await fetchTrending(endpoint);
      const products = payload.products || [];

      if (!products.length) {
        track.innerHTML = '';
        showState(state, 'No hay productos trending disponibles por ahora.', true);
        return;
      }

      renderBrand(root, payload, settings);
      renderProducts(track, products, settings);
      bindAnalytics(root);
      showState(state, '', false);

      trackPromotion('or_view_promotion', root, {
        id: 'hub_gomotors_block',
        title: settings.brandName || 'Go Motors',
        price: 0,
        position: 1,
      });
    } catch (error) {
      console.warn('[or-hub-ad]', error);
      track.innerHTML = '';
      showState(
        state,
        'No pudimos cargar productos de Go Motors. Revisa que la URL del Hub esté activa.',
        true
      );
    }
  }

  class OrHubAd extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') return;
      this.dataset.loaded = 'true';
      loadWidget(this);
    }
  }

  if (!customElements.get('or-hub-ad')) {
    customElements.define('or-hub-ad', OrHubAd);
  }
})();
